import { useEffect, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { MoreToggle } from "../../components/MoreToggle";
import { useProjectCube } from "../../state";
import { usePublicaties, type Publicatie } from "../../state/publicaties";
import { afstand, datum, splits } from "./bekendmakingenFormat";
import { A, Info, KV, SubHeader } from "./util";

const TOON = 5;

const Regel = ({ p }: { p: Publicatie }) => (
  <div className="py-1">
    <A href={p.url}>{p.titel}</A>
    <div className="text-sm text-gray-600">
      {[p.creator, datum(p.gepubliceerdOp), afstand(p.afstandMeter)].filter(Boolean).join(" · ")}
    </div>
  </div>
);

/** Eigen inklapper per lijst: beleid en vergunningen zijn los te lezen. */
const Lijst = ({ kop, rijen }: { kop: string; rijen: Publicatie[] }) => {
  const [open, setOpen] = useState(false);
  if (!rijen.length) return null;
  return (
    <KV k={`${kop} (${rijen.length})`}>
      {(open ? rijen : rijen.slice(0, TOON)).map((p) => (
        <Regel key={p.publicatieId} p={p} />
      ))}
      {rijen.length > TOON && <MoreToggle open={open} setOpen={setOpen} />}
    </KV>
  );
};

/**
 * Officiële bekendmakingen rond het project, uit het publicatieregister van de
 * omgevingsmonitor. Alleen zichtbaar binnen een project: buiten die context is
 * er geen perceel om een straal omheen te leggen.
 *
 * Twee lijsten, want het zijn twee soorten nieuws. Beleid — een omgevingsvisie,
 * een verordening — zegt wat er mag gaan gelden; een vergunning zegt wat er
 * werkelijk gebeurt, meestal op het perceel ernaast. Door elkaar gezet verliest
 * het beleid het altijd: dat hangt aan een gemeente en de vergunning aan een
 * adres, dus op afstand staat de dakkapel bovenaan.
 */
export const Bekendmakingen = () => {
  const projectId = useProjectCube((s) => s.projectId);
  const [state, publicaties, load] = usePublicaties(
    useShallow((s) => [s.state, s.publicaties, s.load])
  );

  useEffect(() => {
    if (projectId != null) load(projectId);
  }, [projectId, load]);

  // Geen project, een storing bij de monitor, of eenvoudigweg niets in de
  // buurt: in alle drie de gevallen is een lege sectie eerlijker dan een
  // foutmelding over een bron die de lezer niet kent.
  if (projectId == null || state === "error" || !publicaties?.length) return null;

  const { beleid, vergunningen } = splits(publicaties);
  return (
    <>
      <SubHeader>Bekendmakingen</SubHeader>
      <Info as="dl">
        <Lijst kop="Beleid en plannen" rijen={beleid} />
        <Lijst kop="Vergunningen en meldingen" rijen={vergunningen} />
      </Info>
    </>
  );
};
